import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  FormGroup,
  Container,
} from "reactstrap";
import { Line } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { GetGraphData } from "action/graph";
import moment from "moment/moment";
import { useDispatch, useSelector } from "react-redux";
import { getTenantList } from "action/plans";
import { GetTenants } from "constants/ReducerConstants/Constants";
import { DATE_RANGE_OPTIONS } from "components/constants";
import Select from "react-select";

function DashboardAnalytics() {
  const dispatch = useDispatch();
  const tenantList =
    useSelector((state) => state?.TenantReducer?.tenantList?.data?.tenants) ||
    [];
  const [chartData, setChartData] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("All");
  const [dateRange, setDateRange] = useState("30");
  const [openCustomModal, setOpenCustomModal] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    getGraphData();
  }, [selectedTenant, dateRange]);

  // Fetch tenant list on component mount
  useEffect(() => {
    //set the query, pagenum ,sort as we don't need to add query and sort set ""
    getTenantList(dispatch, "", 1, "");
    return () => {
      dispatch({ type: GetTenants, payload: {} }); //unmount the data
    };
  }, []);

  const tenantOptions = useMemo(() => {
    return [
      { value: "All", label: "All" },
      ...tenantList.map((tenant) => ({
        value: tenant.ID,
        label: `${tenant.firstName}${tenant.lastName ? ` ${tenant.lastName}` : ""}`,
      })),
    ];
  }, [tenantList]);

  const getGraphData = async (customStartDate = null, customEndDate = null) => {
    try {
      let startDateToSend;
      let endDateToSend;

      if (customStartDate && customEndDate) {
        startDateToSend = customStartDate;
        endDateToSend = customEndDate;
      } else if (
        dateRange === "custom" &&
        customDateRange.startDate &&
        customDateRange.endDate
      ) {
        startDateToSend = customDateRange.startDate;
        endDateToSend = customDateRange.endDate;
      } else {
        endDateToSend = moment().format("YYYY-MM-DD");
        startDateToSend = moment()
          .subtract(Number(dateRange) - 1, "days")
          .format("YYYY-MM-DD");
      }

      //send the type if we want by tenant id then sebd type = tenant_credits_graph otherwise all_credits_graph
      const filter = {
        type:
          selectedTenant === "All"
            ? "ALL_CREDITS_GRAPH"
            : "TENANT_CREDITS_GRAPH",
        startDate: startDateToSend,
        endDate: endDateToSend,
      };

      if (selectedTenant !== "All") {
        filter.tenantID = selectedTenant;
      }

      const response = await GetGraphData(filter);
      if (response?.code === 200 && response?.status === "SUCCESS") {
        const graphData = response?.data?.CampaignsCreditCount || [];
        setChartData(Array.isArray(graphData) ? graphData : []);
      } else {
        setChartData([]);
      }
    } catch (error) {
      console.log("Graph API Error:", error);
      setChartData([]);
    }
  };

  // for filter date we can send backend yyyy-dd-mm format start date (current date )and end date
  // Create all dates between startDate and endDate, and fill missing dates with credits = 0
  const filteredData = useMemo(() => {
    if (!Array.isArray(chartData)) return [];

    const dataMap = {};
    chartData.forEach((item) => {
      dataMap[item.date] = item.credits;
    });

    const start = customDateRange.startDate
      ? moment(customDateRange.startDate)
      : moment().subtract(Number(dateRange) - 1, "days");

    const end = customDateRange.endDate
      ? moment(customDateRange.endDate)
      : moment();

    const allDates = [];
    const current = start.clone();

    while (current.isSameOrBefore(end, "day")) {
      const dateStr = current.format("YYYY-MM-DD");

      allDates.push({
        date: dateStr,
        credits: dataMap[dateStr] || 0,
      });
      current.add(1, "day");
    }
    return allDates;
  }, [chartData, dateRange, customDateRange]);

  //styling for the chart
  const data = {
    labels: filteredData.map(
      (item) =>
        `${moment(item.date).format("dd").charAt(0)} ${moment(item.date).format("DD MMM")}`,
    ),
    datasets: [
      {
        label: "Calendar Used",
        data: filteredData.map((item) => item.credits),
        borderColor: "#11cdef",
        backgroundColor: "rgba(94, 114, 228, 0.1)",
        borderWidth: 2,
        pointBackgroundColor: "#11cdef",
        pointBorderColor: "#11cdef",
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.4,
        fill: false, // set true if want bg fill
        // Show dotted line only when credit > 0
        segment: {
          borderDash: (ctx) => {
            const y0 = ctx.p0.parsed.y;
            const y1 = ctx.p1.parsed.y;
            // If either point has credits > 0, make that segment dotted
            if (y0 > 0 || y1 > 0) {
              return [6, 4]; // dotted line
            }
            return undefined; // solid line
          },
        },
      },
    ],
  };

  const options = {
    //automatically get adjust the size
    responsive: true,
    //normally the chart has ratio of width : height = 2 : 1
    maintainAspectRatio: false, //when we set the false then it take default div size
    //title at top of the chart
    legend: {
      display: true,
      position: "top",
    },
    // Tooltip callback
    tooltips: {
      callbacks: {
        title: function (tooltipItem, data) {
          return "Date: " + data.labels[tooltipItem[0].index];
        },
        label: function (tooltipItem) {
          const item = filteredData[tooltipItem.index];
          return ["Calendars: " + item.credits];
        },
      },
    },
    //on y axis start from 0
    scales: {
      //internally chart.js package do human friendly interval to show the data from the min value and max value
      yAxes: [
        {
          //do not display grid lines on y-axis
          gridLines: {
            drawBorder: false,
            color: "#e9ecef",
            zeroLineColor: "#e9ecef",
          },

          ticks: {
            beginAtZero: true, //start from 0 always
            fontColor: "#8898aa",
            padding: 10,
            precision: 0, //do not set the decimal value
          },
          // scaleLabel: {
          //   display: true,
          //   labelString: "Credits Used", //if we want to show label on y axis
          // },
        },
      ],
      xAxes: [
        {
          //on x-axis if we dont want to show grid || lines then display set to false
          display: true,
          gridLines: {
            display: false,
            drawBorder: true,
          },
          ticks: {
            display: true,
            fontSize: 11,
            fontColor: "#8898aa",
            padding: 10,

            // Automatically skip labels for large date ranges
            autoSkip: true,
            maxTicksLimit:
              Number(dateRange) <= 7
                ? 7
                : Number(dateRange) <= 30
                  ? 30
                  : Number(dateRange) <= 90
                    ? 45
                    : 30, // Show max 30 labels for custom range, adjust as needed

            // Rotate labels slightly to display the date properly
            minRotation: Number(dateRange) > 30 ? 45 : 0,
            maxRotation: Number(dateRange) > 30 ? 45 : 0,
          },
        },
      ],
    },
  };

  const handleApplyCustomDate = () => {
    if (!tempDateRange.startDate || !tempDateRange.endDate) return;
    //format the start and end date as backend accepted yyyy-mm-dd
    const formattedStartDate = moment(tempDateRange.startDate).format(
      "YYYY-MM-DD",
    );
    const formattedEndDate = moment(tempDateRange.endDate).format("YYYY-MM-DD");
    const appliedRange = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
    setCustomDateRange(appliedRange);
    setDateRange("custom");
    setOpenCustomModal(false);
    getGraphData(formattedStartDate, formattedEndDate);
    setTempDateRange({
      startDate: null,
      endDate: null,
    });
  };

  const handleCancelCustomDate = () => {
    setOpenCustomModal(false);
    //after close set default date range as 30
    setDateRange("30");
    setTempDateRange({
      startDate: null,
      endDate: null,
    });
    setCustomDateRange({
      startDate: null,
      endDate: null,
    });
  };

  const handleDateRangeChange = (selectedOption) => {
    const value = selectedOption?.value;

    if (value === "custom") {
      setDateRange("custom");
      setOpenCustomModal(true);
      return;
    }
    setDateRange(value);
    setCustomDateRange({
      startDate: null,
      endDate: null,
    });
  };

  // Custom Chart.js plugin to draw data labels above each point
  const customLabelPlugin = {
    afterDatasetsDraw: function (chart) {
      //This is used to manually draw text on the chart.
      const ctx = chart.ctx;
      // Loop through all datasets in the chart.
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        // Loop through each point in the dataset.
        meta.data.forEach((point, index) => {
          // get the values 5, 10, 25
          const value = dataset.data[index];
          // Skip if the value is 0, null, undefined, or false.
          if (!value) return;
          // Save the current canvas state so our changes        
          ctx.save();     
          ctx.font = "bold 0.8rem";
          ctx.fillStyle = "#000";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(value, point._model.x, point._model.y - 8);
          ctx.restore();
        });
      });
    },
  };

  return (
    <Container className="mt--6" fluid>
      <Card className="shadow">
        <CardBody>
          <Row className="mb-4">
            <Col md="2">
              <Label>Tenants</Label>

              <Select
                options={tenantOptions}
                value={tenantOptions.find(
                  (opt) => opt.value === selectedTenant,
                )}
                onChange={(selected) =>
                  setSelectedTenant(selected?.value || "All")
                }
                isClearable
                isSearchable
                placeholder="Select tenant"
                styles={customStyles}
              />
            </Col>

            <Col md="2">
              <Label>Date Range</Label>
              <Select
                options={DATE_RANGE_OPTIONS}
                value={DATE_RANGE_OPTIONS.find(
                  (opt) => opt.value === dateRange,
                )}
                onChange={handleDateRangeChange}
                styles={customStyles}
                placeholder="Select date range"
                isSearchable={false}
              />
            </Col>
          </Row>

          <h3 className="mb-4">Calendar Sent</h3>

          <div style={{ height: "350px" }}>
            <Line data={data} options={options} plugins={[customLabelPlugin]} />
          </div>
        </CardBody>

        <Modal
          isOpen={openCustomModal}
          toggle={() => setOpenCustomModal(false)}
          centered
        >
          <ModalHeader
            toggle={() => setOpenCustomModal(false)}
            className="pb-2"
          >
            Select Custom Date
          </ModalHeader>

          <ModalBody className="pt-2">
            <Row>
              <Col md="6">
                <FormGroup className="mb-1">
                  <Label className="form-control-label mb-1">Start Date</Label>
                </FormGroup>
                <div className="w-100">
                  <DatePicker
                    placeholderText="Select start date"
                    showIcon
                    icon={<i className="fa-solid fa-calendar-day fa-md mt-2" />}
                    selected={tempDateRange.startDate}
                    onChange={(date) =>
                      setTempDateRange((prev) => ({
                        ...prev,
                        startDate: date,
                      }))
                    }
                    className="form-control"
                    dateFormat="dd MMM yyyy"
                    wrapperClassName="w-100"
                    maxDate={new Date()}
                    style={styles.datePicker}
                  />
                </div>
              </Col>

              <Col md="6">
                <FormGroup className="mb-1">
                  <Label className="form-control-label mb-1">End Date</Label>
                </FormGroup>
                <div className="w-100">
                  <DatePicker
                    placeholderText="Select end date"
                    showIcon
                    icon={<i className="fa-solid fa-calendar-day fa-md mt-2" />}
                    selected={tempDateRange.endDate}
                    onChange={(date) =>
                      setTempDateRange((prev) => ({
                        ...prev,
                        endDate: date,
                      }))
                    }
                    className="form-control"
                    dateFormat="dd MMM yyyy"
                    minDate={tempDateRange.startDate}
                    maxDate={new Date()}
                    wrapperClassName="w-100"
                    style={styles.datePicker}
                  />
                </div>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={handleCancelCustomDate}>
              Cancel
            </Button>

            <Button
              color="info"
              onClick={handleApplyCustomDate}
              disabled={!tempDateRange.startDate || !tempDateRange.endDate}
            >
              Apply
            </Button>
          </ModalFooter>
        </Modal>
      </Card>
    </Container>
  );
}

export default DashboardAnalytics;

const styles = {
  datePicker: {
    width: "100%",
    display: "flex",
  },
};

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    fontSize: "14px", // Font size for the input
    borderColor: state.isFocused ? "#DEE2E6" : "#DEE2E6", // Border color when focused and unfocused
    boxShadow: state.isFocused ? "0 0 0 1px #DEE2E6" : "none", // Remove default shadow on focus
    "&:hover": {
      borderColor: "#DEE2E6", // Border color on hover
    },
    color: "##bfbbcb", // Text color for the input
    height: "47px",
    minHeight: "47px",
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: "14px", // Font size for dropdown items
    color: "##bfbbcb", // Text color for the input
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#8898aa", // Text color for the input
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#8898aa",
  }),
};
